// UltraFlex Admin — User Management (Superadmin → create/edit/deactivate staff logins)
// List → drill-to-card pattern. Manages system login accounts, roles, and site scope.
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSite } from '../../contexts/SiteContext';
import {
  UserPlus, Search, ChevronRight, ArrowLeft, Save, Check, X as XIcon,
  Mail, Shield, MapPin, Trash2, UserCog, KeyRound, CheckCircle2, XCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminUsers() {
  const { token } = useAuth();
  const { allowedSites, hasAllSitesAccess } = useSite();
  const [list, setList] = useState({ items: [], total: 0, page: 1, total_pages: 1 });
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null); // user id
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchList = async () => {
    setLoading(true);
    const params = new URLSearchParams({ page, page_size: 50, active_only: activeOnly });
    if (search) params.append('search', search);
    if (roleFilter) params.append('role', roleFilter);
    try {
      const res = await fetch(`${API}/api/admin-users?${params}`, { headers });
      if (res.ok) setList(await res.json());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); /* eslint-disable-next-line */ }, [search, roleFilter, activeOnly, page]);

  if (selected) {
    return <UserCard userId={selected} onBack={() => { setSelected(null); fetchList(); }} token={token} allowedSites={allowedSites} hasAllSitesAccess={hasAllSitesAccess} />;
  }

  if (showNew) {
    return <NewUserForm onClose={(created) => { setShowNew(false); if (created) fetchList(); }} token={token} allowedSites={allowedSites} hasAllSitesAccess={hasAllSitesAccess} />;
  }

  return (
    <div className="p-6 min-h-screen bg-uf-bg" data-testid="admin-users-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white">User Management</h1>
          <p className="text-uf-textMuted text-sm mt-1">{list.total?.toLocaleString() || 0} accounts · manage system logins, roles and site access</p>
        </div>
        <button
          onClick={() => setShowNew(true)}
          data-testid="new-user-btn"
          className="flex items-center gap-2 bg-uf-red hover:bg-uf-red/90 px-4 py-2 rounded text-white font-medium text-sm"
        >
          <UserPlus className="w-4 h-4" /> New User
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-3 text-uf-textMuted" />
          <input
            placeholder="Search name, email or employee number..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            data-testid="users-search"
            className="w-full bg-uf-surface border border-uf-border pl-10 pr-4 py-2 text-white rounded focus:border-uf-red focus:outline-none"
          />
        </div>
        <select value={roleFilter} onChange={e => { setRoleFilter(e.target.value); setPage(1); }} data-testid="users-role-filter" className="bg-uf-surface border border-uf-border px-3 py-2 text-white rounded">
          <option value="">All roles</option>
          <option value="super_admin">Super Admin</option>
          <option value="developer_admin">Developer Admin</option>
          <option value="area_manager">Area Manager</option>
          <option value="site_manager">Site Manager</option>
          <option value="finance_admin">Finance Admin</option>
          <option value="site_staff">Site Staff</option>
        </select>
        <label className="flex items-center gap-2 px-3 py-2 bg-uf-surface border border-uf-border rounded text-sm text-white cursor-pointer">
          <input type="checkbox" checked={activeOnly} onChange={e => setActiveOnly(e.target.checked)} data-testid="users-active-only" className="accent-uf-red" />
          Active only
        </label>
      </div>

      {/* List */}
      <div className="bg-uf-surface border border-uf-border rounded overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-uf-bg text-uf-textMuted text-xs uppercase tracking-wider font-bold">
          <div className="col-span-3">Name</div>
          <div className="col-span-3">Email</div>
          <div className="col-span-2">Role</div>
          <div className="col-span-2">Sites</div>
          <div className="col-span-1 text-center">Status</div>
          <div className="col-span-1"></div>
        </div>
        {loading && <div className="p-8 text-center text-uf-textMuted">Loading...</div>}
        {!loading && list.items?.length === 0 && <div className="p-8 text-center text-uf-textMuted">No users match your filters</div>}
        {list.items?.map(u => (
          <div
            key={u.id}
            onClick={() => setSelected(u.id)}
            data-testid={`user-row-${u.id}`}
            className="grid grid-cols-12 gap-2 px-4 py-3 border-t border-uf-border hover:bg-uf-bg cursor-pointer items-center"
          >
            <div className="col-span-3 text-white font-medium truncate">{u.first_name} {u.last_name}</div>
            <div className="col-span-3 text-uf-textMuted text-sm truncate">{u.email}</div>
            <div className="col-span-2 text-uf-textMuted text-xs capitalize">
              {u.role?.replace('_', ' ')}
              {u.roles?.length > 0 && <span className="ml-1 text-[10px] bg-uf-red/20 text-uf-red px-1.5 py-0.5 rounded">+{u.roles.length}</span>}
            </div>
            <div className="col-span-2 text-uf-textMuted text-xs truncate">
              {u.site_names?.length ? u.site_names.map(s => s.name).join(', ') : <span className="italic">— none —</span>}
            </div>
            <div className="col-span-1 text-center">
              {u.is_active
                ? <CheckCircle2 className="w-4 h-4 text-green-400 inline" />
                : <XCircle className="w-4 h-4 text-red-400 inline" />}
            </div>
            <div className="col-span-1 text-right"><ChevronRight className="w-4 h-4 text-uf-textMuted inline" /></div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {list.total_pages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 bg-uf-surface border border-uf-border rounded disabled:opacity-50 text-white">Previous</button>
          <span className="text-uf-textMuted">Page {page} of {list.total_pages}</span>
          <button disabled={page >= list.total_pages} onClick={() => setPage(p => p + 1)} className="px-3 py-1 bg-uf-surface border border-uf-border rounded disabled:opacity-50 text-white">Next</button>
        </div>
      )}
    </div>
  );
}

// ---------- User Detail Card ----------

function UserCard({ userId, onBack, token, allowedSites, hasAllSitesAccess }) {
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const [user, setUser] = useState(null);
  const [rolesCatalog, setRolesCatalog] = useState([]);
  const [tab, setTab] = useState('details');
  const [edit, setEdit] = useState({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');

  const loadUser = async () => {
    const [uRes, rRes] = await Promise.all([
      fetch(`${API}/api/admin-users/${userId}`, { headers }),
      fetch(`${API}/api/permissions/roles`, { headers }),
    ]);
    if (uRes.ok) { const u = await uRes.json(); setUser(u); setEdit(u); }
    if (rRes.ok) { const d = await rRes.json(); setRolesCatalog(d.items || []); }
  };
  useEffect(() => { loadUser(); /* eslint-disable-next-line */ }, [userId]);

  const save = async () => {
    setSaving(true); setErr(''); setOk('');
    try {
      const res = await fetch(`${API}/api/admin-users/${userId}`, {
        method: 'PATCH', headers,
        body: JSON.stringify({
          first_name: edit.first_name, last_name: edit.last_name,
          email: edit.email, phone: edit.phone,
          employee_number: edit.employee_number,
          role: edit.role,
          site_id: edit.site_id || null, site_ids: edit.site_ids || [],
          is_active: edit.is_active,
          epos_pin: edit.epos_pin || null, epos_role: edit.epos_role,
        }),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.detail || 'Save failed'); }
      setOk('Saved'); await loadUser();
      setTimeout(() => setOk(''), 2000);
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const resetPassword = async () => {
    const pwd = window.prompt('Set new password for this user:');
    if (!pwd) return;
    setSaving(true); setErr(''); setOk('');
    try {
      const res = await fetch(`${API}/api/admin-users/${userId}`, {
        method: 'PATCH', headers,
        body: JSON.stringify({ password: pwd }),
      });
      if (!res.ok) throw new Error('Password update failed');
      setOk('Password updated');
      setTimeout(() => setOk(''), 2000);
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const deactivate = async () => {
    if (!window.confirm('Deactivate this user account? They will no longer be able to log in.')) return;
    const res = await fetch(`${API}/api/admin-users/${userId}`, { method: 'DELETE', headers });
    if (res.ok) onBack();
    else { const j = await res.json().catch(() => ({})); setErr(j.detail || 'Deactivation failed'); }
  };

  const toggleRole = async (roleId) => {
    const current = new Set(user.roles.map(r => r.id));
    if (current.has(roleId)) current.delete(roleId); else current.add(roleId);
    const res = await fetch(`${API}/api/admin-users/${userId}/roles`, {
      method: 'POST', headers,
      body: JSON.stringify({ role_ids: Array.from(current) }),
    });
    if (res.ok) { setOk('Roles updated'); await loadUser(); setTimeout(() => setOk(''), 1500); }
  };

  const toggleSite = (siteId) => {
    const sids = new Set(edit.site_ids || []);
    if (sids.has(siteId)) sids.delete(siteId); else sids.add(siteId);
    setEdit(e => ({ ...e, site_ids: Array.from(sids) }));
  };

  if (!user) return <div className="p-6 text-uf-textMuted">Loading user...</div>;

  return (
    <div className="p-6 min-h-screen bg-uf-bg" data-testid="user-card">
      <button onClick={onBack} className="flex items-center gap-2 text-uf-textMuted hover:text-white text-sm mb-6" data-testid="back-to-users">
        <ArrowLeft className="w-4 h-4" /> Back to users list
      </button>

      {/* Summary */}
      <div className="bg-uf-surface border border-uf-border rounded p-5 mb-5">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">{user.first_name} {user.last_name}</h1>
            <div className="text-uf-textMuted text-sm mt-1 flex items-center gap-3 flex-wrap">
              <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {user.email}</span>
              {user.employee_number && <span className="flex items-center gap-1"><UserCog className="w-3.5 h-3.5" /> {user.employee_number}</span>}
              <span className="flex items-center gap-1 capitalize"><Shield className="w-3.5 h-3.5" /> {user.role?.replace('_', ' ')}</span>
              <span className={user.is_active ? 'text-green-400' : 'text-red-400'}>{user.is_active ? 'Active' : 'Inactive'}</span>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={resetPassword} className="text-sm px-3 py-1.5 bg-uf-bg border border-uf-border hover:border-uf-red rounded text-white flex items-center gap-1.5" data-testid="reset-password-btn">
              <KeyRound className="w-3.5 h-3.5" /> Reset Password
            </button>
            {user.is_active && (
              <button onClick={deactivate} className="text-sm px-3 py-1.5 bg-red-500/10 border border-red-500/30 hover:bg-red-500/20 rounded text-red-400 flex items-center gap-1.5" data-testid="deactivate-user-btn">
                <Trash2 className="w-3.5 h-3.5" /> Deactivate
              </button>
            )}
          </div>
        </div>
        {err && <div className="mt-3 text-sm text-red-400 bg-red-500/10 p-2 rounded">{err}</div>}
        {ok && <div className="mt-3 text-sm text-green-400 bg-green-500/10 p-2 rounded flex items-center gap-2"><Check className="w-4 h-4" /> {ok}</div>}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-uf-border">
        {[
          { id: 'details', label: 'Details' },
          { id: 'roles', label: 'Roles' },
          { id: 'sites', label: 'Site Access' },
          { id: 'epos', label: 'EPOS' },
          { id: 'permissions', label: `Effective Permissions (${user.effective_permissions?.length || 0})` },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`user-tab-${t.id}`}
            className={`px-4 py-2 text-sm transition-colors ${tab === t.id ? 'text-uf-red border-b-2 border-uf-red -mb-px' : 'text-uf-textMuted hover:text-white'}`}
          >{t.label}</button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === 'details' && (
        <div className="bg-uf-surface border border-uf-border rounded p-5 space-y-4 max-w-3xl">
          <div className="grid grid-cols-2 gap-4">
            <Field label="First Name" value={edit.first_name} onChange={v => setEdit(e => ({ ...e, first_name: v }))} testid="user-firstname" />
            <Field label="Last Name" value={edit.last_name} onChange={v => setEdit(e => ({ ...e, last_name: v }))} testid="user-lastname" />
            <Field label="Email" value={edit.email} onChange={v => setEdit(e => ({ ...e, email: v }))} testid="user-email" />
            <Field label="Phone" value={edit.phone || ''} onChange={v => setEdit(e => ({ ...e, phone: v }))} testid="user-phone" />
            <Field label="Employee Number" value={edit.employee_number || ''} onChange={v => setEdit(e => ({ ...e, employee_number: v }))} testid="user-empnum" />
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-uf-textMuted mb-1">Role (legacy enum)</label>
              <select value={edit.role || ''} onChange={e => setEdit(x => ({ ...x, role: e.target.value }))} data-testid="user-role-select" className="w-full bg-uf-bg border border-uf-border px-3 py-2 text-white rounded">
                <option value="super_admin">Super Admin</option>
                <option value="developer_admin">Developer Admin</option>
                <option value="area_manager">Area Manager</option>
                <option value="site_manager">Site Manager</option>
                <option value="finance_admin">Finance Admin</option>
                <option value="site_staff">Site Staff</option>
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2 pt-2">
            <input type="checkbox" checked={!!edit.is_active} onChange={e => setEdit(x => ({ ...x, is_active: e.target.checked }))} data-testid="user-active-toggle" className="accent-uf-red" />
            <span className="text-white text-sm">Account active</span>
          </div>
          <button onClick={save} disabled={saving} data-testid="save-user-btn" className="bg-uf-red hover:bg-uf-red/90 disabled:opacity-50 px-4 py-2 rounded text-white text-sm flex items-center gap-2">
            <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      )}

      {tab === 'roles' && (
        <div className="bg-uf-surface border border-uf-border rounded p-5 max-w-3xl">
          <p className="text-uf-textMuted text-sm mb-4">Assign stackable roles. Permissions from all assigned roles are merged.</p>
          <div className="space-y-2">
            {rolesCatalog.map(role => {
              const active = user.roles?.some(r => r.id === role.id);
              return (
                <div
                  key={role.id}
                  onClick={() => toggleRole(role.id)}
                  data-testid={`role-toggle-${role.id}`}
                  className={`flex items-center justify-between p-3 rounded border cursor-pointer transition-colors ${active ? 'bg-uf-red/10 border-uf-red' : 'bg-uf-bg border-uf-border hover:border-uf-red/50'}`}
                >
                  <div>
                    <div className="text-white font-medium text-sm flex items-center gap-2">
                      {role.name}
                      {role.is_system && <span className="text-[10px] px-1.5 py-0.5 bg-uf-textMuted/20 text-uf-textMuted rounded uppercase">System</span>}
                    </div>
                    <div className="text-uf-textMuted text-xs mt-0.5">{role.description}</div>
                    <div className="text-uf-textMuted text-[10px] mt-1">{role.permissions?.length || 0} permissions</div>
                  </div>
                  {active && <Check className="w-5 h-5 text-uf-red" />}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === 'sites' && (
        <div className="bg-uf-surface border border-uf-border rounded p-5 max-w-3xl">
          <p className="text-uf-textMuted text-sm mb-4">Click a site to toggle access. Sites with a red highlight are currently allowed; greyed-out sites are blocked. Super Admin role automatically sees all sites regardless.</p>
          <div className="grid grid-cols-2 gap-2">
            {allowedSites.map(site => {
              const active = (edit.site_ids || []).includes(site.id);
              return (
                <div
                  key={site.id}
                  onClick={() => toggleSite(site.id)}
                  data-testid={`site-toggle-${site.id}`}
                  className={`flex items-center justify-between p-3 rounded border cursor-pointer transition-colors ${active ? 'bg-uf-red/10 border-uf-red' : 'bg-uf-bg border-uf-border hover:border-uf-red/50 opacity-70'}`}
                >
                  <div className="flex items-center gap-2">
                    <MapPin className={`w-4 h-4 ${active ? 'text-uf-red' : 'text-uf-textMuted'}`} />
                    <span className="text-white text-sm font-medium">{site.name}</span>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider font-semibold ${active ? 'text-uf-red' : 'text-uf-textMuted'}`}>{active ? 'ALLOWED' : 'BLOCKED'}</span>
                </div>
              );
            })}
          </div>
          <div className="mt-4 pt-4 border-t border-uf-border">
            <label className="block text-xs font-semibold uppercase tracking-wider text-uf-textMuted mb-1">Primary Home Site</label>
            <select value={edit.site_id || ''} onChange={e => setEdit(x => ({ ...x, site_id: e.target.value || null }))} data-testid="user-primary-site" className="w-full bg-uf-bg border border-uf-border px-3 py-2 text-white rounded">
              <option value="">— none —</option>
              {allowedSites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <button onClick={save} disabled={saving} data-testid="save-user-sites" className="mt-4 bg-uf-red hover:bg-uf-red/90 disabled:opacity-50 px-4 py-2 rounded text-white text-sm flex items-center gap-2">
            <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save Site Access'}
          </button>
        </div>
      )}

      {tab === 'epos' && (
        <div className="bg-uf-surface border border-uf-border rounded p-5 max-w-3xl space-y-4">
          <p className="text-uf-textMuted text-sm">EPOS till login uses employee number + PIN (separate from email/password).</p>
          <Field label="EPOS PIN (4-6 digits)" value={edit.epos_pin || ''} onChange={v => setEdit(e => ({ ...e, epos_pin: v }))} testid="user-epos-pin" />
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-uf-textMuted mb-1">EPOS Role</label>
            <select value={edit.epos_role || 'cashier'} onChange={e => setEdit(x => ({ ...x, epos_role: e.target.value }))} data-testid="user-epos-role" className="w-full bg-uf-bg border border-uf-border px-3 py-2 text-white rounded">
              <option value="cashier">Cashier</option>
              <option value="supervisor">Supervisor</option>
              <option value="manager">Manager</option>
            </select>
          </div>
          <button onClick={save} disabled={saving} data-testid="save-user-epos" className="bg-uf-red hover:bg-uf-red/90 disabled:opacity-50 px-4 py-2 rounded text-white text-sm flex items-center gap-2">
            <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save EPOS'}
          </button>
        </div>
      )}

      {tab === 'permissions' && (
        <div className="bg-uf-surface border border-uf-border rounded p-5 max-w-3xl">
          <p className="text-uf-textMuted text-sm mb-3">Full effective permission list (merged from assigned roles + legacy role enum).</p>
          {user.has_all_sites_access && (
            <div className="bg-uf-red/10 border border-uf-red/30 text-uf-red text-sm p-2 rounded mb-3 flex items-center gap-2">
              <Shield className="w-4 h-4" /> This user has All Sites access.
            </div>
          )}
          <div className="grid grid-cols-2 gap-1 text-xs font-mono">
            {(user.effective_permissions || []).map(p => (
              <div key={p} className="bg-uf-bg border border-uf-border rounded px-2 py-1 text-uf-text-muted" data-testid={`perm-${p}`}>{p}</div>
            ))}
          </div>
          {(!user.effective_permissions || user.effective_permissions.length === 0) && (
            <div className="text-uf-textMuted italic text-sm">No permissions granted yet. Assign roles on the Roles tab.</div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- New User Form ----------

function NewUserForm({ onClose, token, allowedSites, hasAllSitesAccess }) {
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const [form, setForm] = useState({
    email: '', first_name: '', last_name: '',
    phone: '', employee_number: '',
    role: 'site_staff', site_id: '', site_ids: [],
    password: '', must_reset_password: true,
    epos_pin: '', epos_role: 'cashier',
    role_ids: [],
  });
  const [rolesCatalog, setRolesCatalog] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    fetch(`${API}/api/permissions/roles`, { headers }).then(r => r.ok ? r.json() : { items: [] }).then(d => setRolesCatalog(d.items || []));
    // eslint-disable-next-line
  }, []);

  const toggleSite = (siteId) => {
    const sids = new Set(form.site_ids || []);
    if (sids.has(siteId)) sids.delete(siteId); else sids.add(siteId);
    setForm(f => ({ ...f, site_ids: Array.from(sids) }));
  };
  const toggleRole = (rid) => {
    const rs = new Set(form.role_ids || []);
    if (rs.has(rid)) rs.delete(rid); else rs.add(rid);
    setForm(f => ({ ...f, role_ids: Array.from(rs) }));
  };

  const create = async () => {
    setSaving(true); setErr('');
    try {
      const body = {
        ...form,
        site_id: form.site_id || null,
      };
      const res = await fetch(`${API}/api/admin-users`, { method: 'POST', headers, body: JSON.stringify(body) });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.detail || 'Create failed'); }
      onClose(true);
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-6 min-h-screen bg-uf-bg" data-testid="new-user-form">
      <button onClick={() => onClose(false)} className="flex items-center gap-2 text-uf-textMuted hover:text-white text-sm mb-6" data-testid="cancel-new-user">
        <ArrowLeft className="w-4 h-4" /> Back to users list
      </button>
      <h1 className="text-2xl font-bold text-white mb-5">Create New User</h1>
      <div className="bg-uf-surface border border-uf-border rounded p-5 max-w-3xl space-y-5">
        {err && <div className="text-sm text-red-400 bg-red-500/10 p-2 rounded">{err}</div>}

        <section>
          <h2 className="text-white font-semibold mb-3 text-sm">Identity</h2>
          <div className="grid grid-cols-2 gap-4">
            <Field label="First Name *" value={form.first_name} onChange={v => setForm(f => ({ ...f, first_name: v }))} testid="new-firstname" />
            <Field label="Last Name *" value={form.last_name} onChange={v => setForm(f => ({ ...f, last_name: v }))} testid="new-lastname" />
            <Field label="Email *" value={form.email} onChange={v => setForm(f => ({ ...f, email: v }))} testid="new-email" />
            <Field label="Phone" value={form.phone} onChange={v => setForm(f => ({ ...f, phone: v }))} testid="new-phone" />
            <Field label="Employee Number" value={form.employee_number} onChange={v => setForm(f => ({ ...f, employee_number: v }))} testid="new-empnum" />
            <Field label="Temporary Password *" value={form.password} onChange={v => setForm(f => ({ ...f, password: v }))} type="password" testid="new-password" />
          </div>
          <label className="flex items-center gap-2 text-white text-sm mt-3">
            <input type="checkbox" checked={form.must_reset_password} onChange={e => setForm(f => ({ ...f, must_reset_password: e.target.checked }))} data-testid="new-must-reset" className="accent-uf-red" />
            Require password reset on first login
          </label>
        </section>

        <section>
          <h2 className="text-white font-semibold mb-3 text-sm">System Role & Scope</h2>
          <div className="grid grid-cols-2 gap-4 mb-3">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-uf-textMuted mb-1">Role</label>
              <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} data-testid="new-role" className="w-full bg-uf-bg border border-uf-border px-3 py-2 text-white rounded">
                <option value="super_admin">Super Admin</option>
                <option value="developer_admin">Developer Admin</option>
                <option value="area_manager">Area Manager</option>
                <option value="site_manager">Site Manager</option>
                <option value="finance_admin">Finance Admin</option>
                <option value="site_staff">Site Staff</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-uf-textMuted mb-1">Primary Home Site</label>
              <select value={form.site_id} onChange={e => setForm(f => ({ ...f, site_id: e.target.value }))} data-testid="new-primary-site" className="w-full bg-uf-bg border border-uf-border px-3 py-2 text-white rounded">
                <option value="">— none —</option>
                {allowedSites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-uf-textMuted mb-2">Allowed Sites (for data visibility)</label>
          <div className="grid grid-cols-2 gap-2">
            {allowedSites.map(site => {
              const active = form.site_ids.includes(site.id);
              return (
                <div
                  key={site.id}
                  onClick={() => toggleSite(site.id)}
                  data-testid={`new-site-${site.id}`}
                  className={`flex items-center justify-between p-2.5 rounded border cursor-pointer text-sm ${active ? 'bg-uf-red/10 border-uf-red text-white' : 'bg-uf-bg border-uf-border text-uf-textMuted hover:border-uf-red/50'}`}
                >
                  <span className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> {site.name}</span>
                  {active && <Check className="w-4 h-4 text-uf-red" />}
                </div>
              );
            })}
          </div>
        </section>

        <section>
          <h2 className="text-white font-semibold mb-3 text-sm">Role Templates (optional)</h2>
          <p className="text-uf-textMuted text-xs mb-2">Stackable role grants. Permissions from all assigned roles are merged.</p>
          <div className="grid grid-cols-2 gap-2">
            {rolesCatalog.map(r => {
              const on = form.role_ids.includes(r.id);
              return (
                <div key={r.id} onClick={() => toggleRole(r.id)} data-testid={`new-role-${r.id}`} className={`p-2.5 rounded border cursor-pointer text-sm ${on ? 'bg-uf-red/10 border-uf-red text-white' : 'bg-uf-bg border-uf-border text-uf-textMuted hover:border-uf-red/50'}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{r.name}</span>
                    {on && <Check className="w-4 h-4 text-uf-red" />}
                  </div>
                  <div className="text-[10px] mt-0.5">{r.permissions?.length || 0} permissions</div>
                </div>
              );
            })}
          </div>
        </section>

        <section>
          <h2 className="text-white font-semibold mb-3 text-sm">EPOS Access (optional)</h2>
          <div className="grid grid-cols-2 gap-4">
            <Field label="EPOS PIN (4-6 digits)" value={form.epos_pin} onChange={v => setForm(f => ({ ...f, epos_pin: v }))} testid="new-epos-pin" />
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-uf-textMuted mb-1">EPOS Role</label>
              <select value={form.epos_role} onChange={e => setForm(f => ({ ...f, epos_role: e.target.value }))} data-testid="new-epos-role" className="w-full bg-uf-bg border border-uf-border px-3 py-2 text-white rounded">
                <option value="cashier">Cashier</option>
                <option value="supervisor">Supervisor</option>
                <option value="manager">Manager</option>
              </select>
            </div>
          </div>
        </section>

        <div className="flex justify-end gap-3 pt-2">
          <button onClick={() => onClose(false)} className="px-4 py-2 bg-uf-bg border border-uf-border rounded text-white text-sm" data-testid="new-user-cancel">Cancel</button>
          <button onClick={create} disabled={saving || !form.email || !form.first_name || !form.last_name || !form.password} data-testid="new-user-create" className="bg-uf-red hover:bg-uf-red/90 disabled:opacity-50 px-4 py-2 rounded text-white text-sm flex items-center gap-2">
            <UserPlus className="w-4 h-4" /> {saving ? 'Creating…' : 'Create User'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Field helper ----------

function Field({ label, value, onChange, testid, type = 'text' }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-uf-textMuted mb-1">{label}</label>
      <input
        type={type}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-uf-bg border border-uf-border px-3 py-2 text-white rounded focus:border-uf-red focus:outline-none"
      />
    </div>
  );
}
